import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search, Crosshair, Layers } from 'lucide-react';
import CommunityMap from '../../components/CommunityMap/CommunityMap';
import ThreadPostCard from '../../components/CommunityThread/ThreadPostCard';
import ThreadDetailModal from '../../components/ThreadDetailModal/ThreadDetailModal';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
const ACTING_USER_ID = 1001;

async function geocodePlace(query) {
  if (!MAPBOX_TOKEN || !query.trim()) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
    query.trim(),
  )}.json?access_token=${MAPBOX_TOKEN}&limit=1`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const json = await res.json();
  const f = json.features?.[0];
  if (!f) return null;
  const [lon, lat] = f.center;
  return { lat, lon, placeName: f.place_name };
}

export default function CommunityMapPage() {
  const [searchQuery, setSearchQuery] = useState('Seoul');
  const [panelThreads, setPanelThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showBoards, setShowBoards] = useState(true);
  const [mapCenter, setMapCenter] = useState({ latitude: 37.4979, longitude: 127.0276, zoom: 10 });
  const dataSource = useCommunityDataSource();

  const mapThreadsQuery = useQuery({
    queryKey: ['communityMapThreads', dataSource],
    queryFn: () => provider.getRegionalThreadsForMap(),
  });

  const boardsQuery = useQuery({
    queryKey: ['communityMapBoards', dataSource, mapCenter.latitude, mapCenter.longitude],
    queryFn: () =>
      provider.getBoards({
        latitude: mapCenter.latitude,
        longitude: mapCenter.longitude,
        radius_m: 15000,
        limit: 20,
      }),
    enabled: showBoards,
  });

  const allRegional = mapThreadsQuery.data || [];
  const boards = boardsQuery.data?.data || [];

  const handleGeocodeSearch = async (e) => {
    e.preventDefault();
    const place = await geocodePlace(searchQuery);
    if (place) {
      setMapCenter({ latitude: place.lat, longitude: place.lon, zoom: 12 });
      const nearby = await provider.getThreadsNearby(
        {
          latitude: place.lat,
          longitude: place.lon,
          radius_m: 8000,
          limit: 50,
        },
        ACTING_USER_ID,
      );
      setPanelThreads(nearby.data || []);
    }
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lon } = pos.coords;
      setMapCenter({ latitude: lat, longitude: lon, zoom: 12 });
      const nearby = await provider.getThreadsNearby(
        {
          latitude: lat,
          longitude: lon,
          radius_m: 5000,
          limit: 50,
        },
        ACTING_USER_ID,
      );
      setPanelThreads(nearby.data || []);
    });
  };

  const onSelectThreads = useCallback((list) => {
    setPanelThreads(list);
  }, []);

  const displayList = panelThreads.length > 0 ? panelThreads : allRegional;

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-220px)] min-h-[480px]">
      <div className="lg:w-96 flex flex-col gap-3 shrink-0">
        <form onSubmit={handleGeocodeSearch} className="flex gap-2">
          <input
            className="flex-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm"
            placeholder="Search place (Mapbox)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="p-2 border border-dark-border rounded hover:border-primary" title="Geocode">
            <Search size={18} />
          </button>
          <button
            type="button"
            className="p-2 border border-dark-border rounded hover:border-primary"
            onClick={handleMyLocation}
            title="My location"
          >
            <Crosshair size={18} />
          </button>
        </form>

        <div className="flex items-center justify-between text-xs text-dark-text-muted">
          <span className="flex items-center gap-1">
            <MapPin size={14} />
            {displayList.length} thread(s) — click cluster or pin
          </span>
          <button
            type="button"
            className={`flex items-center gap-1 px-2 py-1 rounded border ${
              showBoards ? 'border-primary text-primary' : 'border-dark-border'
            }`}
            onClick={() => setShowBoards((v) => !v)}
          >
            <Layers size={12} /> Boards ({boards.length})
          </button>
        </div>

        {showBoards && boards.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {boards.map((b) => (
              <span
                key={b.id}
                className="text-[10px] px-2 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200"
                title={`${b.thread_count} threads`}
              >
                #{b.name} ({b.thread_count})
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto border border-dark-border rounded-lg bg-dark-card/30">
          {displayList.length === 0 && (
            <p className="p-4 text-dark-text-muted text-sm">No threads to show.</p>
          )}
          {displayList.map((t) => (
            <ThreadPostCard
              key={t.id}
              thread={t}
              actingUserId={ACTING_USER_ID}
              compact
              onAdminDetails={setSelectedId}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-[400px] rounded-lg overflow-hidden border border-dark-border">
        <CommunityMap
          key={`${mapCenter.latitude}-${mapCenter.longitude}-${mapCenter.zoom}`}
          threads={allRegional}
          onSelectThreads={onSelectThreads}
          initialViewState={mapCenter}
        />
      </div>

      {selectedId && (
        <ThreadDetailModal
          threadId={selectedId}
          actingUserId={ACTING_USER_ID}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
